import React, { useState, useRef, useEffect } from 'react';
import { X, Check, Move, ZoomIn, ZoomOut } from 'lucide-react';

interface ImageCropModalProps {
  imageFile: File;
  onCropComplete: (croppedFile: File) => void;
  onCancel: () => void;
}

export const ImageCropModal: React.FC<ImageCropModalProps> = ({
  imageFile,
  onCropComplete,
  onCancel,
}) => {
  const [imageSrc, setImageSrc] = useState<string>('');
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isReading, setIsReading] = useState(true);
  const [isCompressing, setIsCompressing] = useState(false);
  const [loadError, setLoadError] = useState<string>('');
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [imageNaturalSize, setImageNaturalSize] = useState({ width: 0, height: 0 });

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const readerRef = useRef<FileReader | null>(null);

  // Responsive canvas size - smaller on mobile
  const CANVAS_SIZE = typeof window !== 'undefined' && window.innerWidth < 640 ? 300 : 400;
  const OUTPUT_SIZE = 1080;
  const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB
  const READ_TIMEOUT = 30000; // 30 seconds
  const COMPRESS_THRESHOLD = 1 * 1024 * 1024; // 1MB - compress files larger than this

  // Compress large images before loading with progressive quality
  const compressImage = async (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      img.onload = () => {
        try {
          // Calculate dimensions to reduce file size while maintaining aspect ratio
          let width = img.width;
          let height = img.height;

          // Progressive compression: larger files get more aggressive compression
          const fileSizeMB = file.size / (1024 * 1024);
          let maxDimension: number;
          let quality: number;

          if (fileSizeMB > 10) {
            // Very large files (>10MB): aggressive compression
            maxDimension = 1600;
            quality = 0.7;
          } else if (fileSizeMB > 5) {
            // Large files (5-10MB): moderate compression
            maxDimension = 1800;
            quality = 0.75;
          } else if (fileSizeMB > 2) {
            // Medium files (2-5MB): light compression
            maxDimension = 2048;
            quality = 0.8;
          } else {
            // Small files (1-2MB): minimal compression
            maxDimension = 2048;
            quality = 0.85;
          }

          if (width > maxDimension || height > maxDimension) {
            if (width > height) {
              height = (height / width) * maxDimension;
              width = maxDimension;
            } else {
              width = (width / height) * maxDimension;
              height = maxDimension;
            }
          }

          canvas.width = width;
          canvas.height = height;

          if (ctx) {
            // Enable image smoothing for better quality
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';

            ctx.drawImage(img, 0, 0, width, height);

            // Convert to compressed JPEG with calculated quality
            const compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
            resolve(compressedDataUrl);
          } else {
            reject(new Error('Failed to get canvas context'));
          }
        } catch (error) {
          reject(error);
        } finally {
          // Clean up
          URL.revokeObjectURL(img.src);
        }
      };

      img.onerror = () => {
        URL.revokeObjectURL(img.src);
        reject(new Error('Failed to load image for compression'));
      };

      // Use object URL for faster loading
      img.src = URL.createObjectURL(file);
    });
  };

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    let isCancelled = false;

    const loadImage = async () => {
      // Check file size
      if (imageFile.size > MAX_FILE_SIZE) {
        setLoadError('Image is too large. Please choose an image smaller than 20MB.');
        setIsReading(false);
        return;
      }

      setIsReading(true);
      setLoadError('');

      try {
        let dataUrl: string;

        // Compress large files before loading
        if (imageFile.size > COMPRESS_THRESHOLD) {
          setIsCompressing(true);
          dataUrl = await compressImage(imageFile);
          setIsCompressing(false);
        } else {
          // For smaller files, use FileReader directly
          dataUrl = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            readerRef.current = reader;

            reader.onload = (e) => {
              const result = e.target?.result as string;
              if (result) {
                resolve(result);
              } else {
                reject(new Error('No result from FileReader'));
              }
            };

            reader.onerror = () => reject(new Error('Failed to read file'));
            reader.readAsDataURL(imageFile);
          });
        }

        if (!isCancelled) {
          setImageSrc(dataUrl);
          setIsReading(false);
        }
      } catch (error) {
        if (!isCancelled) {
          console.error('Image loading error:', error);
          setLoadError('Failed to load image. Please try a different file.');
          setIsReading(false);
          setIsCompressing(false);
        }
      }
    };

    // Set timeout
    timeoutId = setTimeout(() => {
      if (!isCancelled) {
        if (readerRef.current) {
          readerRef.current.abort();
        }
        setLoadError('Image loading timed out. The file may be too large or corrupted.');
        setIsReading(false);
      }
    }, READ_TIMEOUT);

    loadImage();

    // Cleanup
    return () => {
      isCancelled = true;
      clearTimeout(timeoutId);
      if (readerRef.current) {
        readerRef.current.abort();
      }
    };
  }, [imageFile]);

  useEffect(() => {
    if (imageSrc && imageRef.current) {
      const img = imageRef.current;
      img.onload = () => {
        setImageNaturalSize({ width: img.naturalWidth, height: img.naturalHeight });

        // Calculate initial scale to fit image in square
        const minDimension = Math.min(img.naturalWidth, img.naturalHeight);
        const initialScale = CANVAS_SIZE / minDimension;
        setScale(initialScale);

        // Center the image
        setPosition({
          x: (CANVAS_SIZE - img.naturalWidth * initialScale) / 2,
          y: (CANVAS_SIZE - img.naturalHeight * initialScale) / 2,
        });

        setImageLoaded(true);
      };
    }
  }, [imageSrc]);

  useEffect(() => {
    if (imageLoaded && canvasRef.current && imageRef.current) {
      drawCanvas();
    }
  }, [imageLoaded, scale, position]);

  const drawCanvas = () => {
    const canvas = canvasRef.current;
    const img = imageRef.current;
    if (!canvas || !img) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear canvas
    ctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);

    // Draw darkened background
    ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
    ctx.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);

    // Save context state
    ctx.save();

    // Create clipping path for the crop area (center square)
    ctx.beginPath();
    ctx.rect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
    ctx.clip();

    // Draw image
    ctx.drawImage(
      img,
      position.x,
      position.y,
      img.naturalWidth * scale,
      img.naturalHeight * scale
    );

    ctx.restore();

    // Draw crop frame
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 2;
    ctx.strokeRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);

    // Draw corner guides
    const cornerLength = 20;
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 3;

    // Top-left
    ctx.beginPath();
    ctx.moveTo(0, cornerLength);
    ctx.lineTo(0, 0);
    ctx.lineTo(cornerLength, 0);
    ctx.stroke();

    // Top-right
    ctx.beginPath();
    ctx.moveTo(CANVAS_SIZE - cornerLength, 0);
    ctx.lineTo(CANVAS_SIZE, 0);
    ctx.lineTo(CANVAS_SIZE, cornerLength);
    ctx.stroke();

    // Bottom-left
    ctx.beginPath();
    ctx.moveTo(0, CANVAS_SIZE - cornerLength);
    ctx.lineTo(0, CANVAS_SIZE);
    ctx.lineTo(cornerLength, CANVAS_SIZE);
    ctx.stroke();

    // Bottom-right
    ctx.beginPath();
    ctx.moveTo(CANVAS_SIZE - cornerLength, CANVAS_SIZE);
    ctx.lineTo(CANVAS_SIZE, CANVAS_SIZE);
    ctx.lineTo(CANVAS_SIZE, CANVAS_SIZE - cornerLength);
    ctx.stroke();
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;

    setPosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y,
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    setIsDragging(true);
    setDragStart({
      x: touch.clientX - position.x,
      y: touch.clientY - position.y,
    });
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;
    e.preventDefault();

    const touch = e.touches[0];
    setPosition({
      x: touch.clientX - dragStart.x,
      y: touch.clientY - dragStart.y,
    });
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  const handleZoomIn = () => {
    setScale(prev => Math.min(prev * 1.2, 5));
  };

  const handleZoomOut = () => {
    setScale(prev => Math.max(prev / 1.2, 0.1));
  };

  const handleCrop = async () => {
    const canvas = canvasRef.current;
    const img = imageRef.current;
    if (!canvas || !img) return;

    // Create output canvas at 1080x1080
    const outputCanvas = document.createElement('canvas');
    outputCanvas.width = OUTPUT_SIZE;
    outputCanvas.height = OUTPUT_SIZE;
    const ctx = outputCanvas.getContext('2d');
    if (!ctx) return;

    // Calculate source rectangle in original image coordinates
    // position is the offset where image is drawn on canvas
    // We need to find which part of the original image corresponds to the visible canvas area

    // Convert canvas coordinates to image coordinates
    const sourceX = -position.x / scale;
    const sourceY = -position.y / scale;
    const sourceWidth = CANVAS_SIZE / scale;
    const sourceHeight = CANVAS_SIZE / scale;

    // Draw the cropped area from source image to output canvas
    ctx.drawImage(
      img,
      sourceX,                    // Source X in original image
      sourceY,                    // Source Y in original image
      sourceWidth,                // Source width in original image
      sourceHeight,               // Source height in original image
      0,                          // Destination X (top-left of output canvas)
      0,                          // Destination Y (top-left of output canvas)
      OUTPUT_SIZE,                // Destination width (1080px)
      OUTPUT_SIZE                 // Destination height (1080px)
    );

    // Convert to blob
    outputCanvas.toBlob((blob) => {
      if (!blob) return;

      // Create new File from blob
      const croppedFile = new File([blob], imageFile.name, {
        type: 'image/png',
        lastModified: Date.now(),
      });

      onCropComplete(croppedFile);
    }, 'image/png', 0.95);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-screen overflow-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Crop Image</h3>
            <p className="text-sm text-gray-500 mt-1">
              Drag to position, use zoom to adjust size
            </p>
          </div>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* Canvas Container */}
          <div
            ref={containerRef}
            className="relative mx-auto bg-gray-100 rounded-lg overflow-hidden"
            style={{ width: CANVAS_SIZE, height: CANVAS_SIZE }}
          >
            <canvas
              ref={canvasRef}
              width={CANVAS_SIZE}
              height={CANVAS_SIZE}
              className="cursor-move touch-none"
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            />

            {/* Hidden image for drawing */}
            {imageSrc && (
              <img
                ref={imageRef}
                src={imageSrc}
                className="hidden"
                alt="Source"
              />
            )}

            {/* Loading and error states */}
            {isReading && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-100 gap-3 p-4">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-300 border-t-blue-600"></div>
                {isCompressing ? (
                  <>
                    <div className="text-gray-600 font-medium">Compressing image...</div>
                    <div className="text-sm text-gray-500 text-center">
                      {(() => {
                        const sizeMB = imageFile.size / (1024 * 1024);
                        if (sizeMB > 10) {
                          return 'Applying strong compression for very large file';
                        } else if (sizeMB > 5) {
                          return 'Applying moderate compression';
                        } else {
                          return 'Optimizing for faster loading';
                        }
                      })()}
                      <br />
                      Original size: {(imageFile.size / (1024 * 1024)).toFixed(1)} MB
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-gray-600 font-medium">Reading image...</div>
                    <div className="text-sm text-gray-500">This may take a moment for large files</div>
                  </>
                )}
              </div>
            )}
            {!isReading && imageSrc && !imageLoaded && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-100 gap-3">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-300 border-t-blue-600"></div>
                <div className="text-gray-600 font-medium">Preparing image...</div>
              </div>
            )}
            {loadError && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-red-50 gap-3 p-4">
                <div className="text-red-600 font-medium text-center">{loadError}</div>
                <button
                  onClick={onCancel}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Close
                </button>
              </div>
            )}
          </div>

          {/* Controls */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Move className="w-4 h-4" />
                <span>Drag image to position</span>
              </div>
              <div className="text-sm text-gray-500">
                Output: 1080×1080px
              </div>
            </div>

            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-600 font-medium">Zoom:</span>
              <button
                onClick={handleZoomOut}
                className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
                disabled={scale <= 0.1}
              >
                <ZoomOut className="w-4 h-4" />
              </button>
              <input
                type="range"
                min="0.1"
                max="5"
                step="0.1"
                value={scale}
                onChange={(e) => setScale(parseFloat(e.target.value))}
                className="flex-1"
              />
              <button
                onClick={handleZoomIn}
                className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
                disabled={scale >= 5}
              >
                <ZoomIn className="w-4 h-4" />
              </button>
              <span className="text-sm text-gray-600 min-w-[3rem] text-right">
                {Math.round(scale * 100)}%
              </span>
            </div>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>Tip:</strong> The image will be cropped to a perfect square (1:1 ratio)
              at 1080×1080 pixels, which is optimal for all social media platforms.
              {imageFile.size > COMPRESS_THRESHOLD && (
                <>
                  {' '}
                  {(() => {
                    const sizeMB = imageFile.size / (1024 * 1024);
                    if (sizeMB > 10) {
                      return 'Very large images (>10MB) are compressed to 1600px with 70% quality for optimal performance.';
                    } else if (sizeMB > 5) {
                      return 'Large images (5-10MB) are compressed to 1800px with 75% quality for faster loading.';
                    } else if (sizeMB > 2) {
                      return 'Medium images (2-5MB) are compressed to 2048px with 80% quality.';
                    } else {
                      return 'Images are automatically compressed for faster loading while maintaining quality.';
                    }
                  })()}
                </>
              )}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleCrop}
            disabled={!imageLoaded || isReading || !!loadError}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Check className="w-4 h-4" />
            <span>Crop & Save</span>
          </button>
        </div>
      </div>
    </div>
  );
};
